import { classifyAspect } from '@/lib/video/creativeSpec';
import type { AspectRatio } from '@/types/video';

/**
 * Что можно выбрать в пикере. Контейнер и кодек сервер приводит к MP4 / H.264
 * сам после загрузки (docs/CREATIVE_SPEC.md), поэтому `.mov` с AirDrop или
 * `.mkv` больше не повод для отказа на сайте. Один список на все пикеры:
 * страница загрузки, студия, замена ролика в брони.
 */
export const VIDEO_EXTENSIONS = [
  '.mp4', '.m4v', '.mov', '.mkv', '.webm', '.avi', '.mts', '.m2ts', '.ts', '.3gp', '.mpg', '.mpeg', '.wmv', '.flv',
] as const;

/** Для `<input type="file" accept>`. */
export const VIDEO_ACCEPT = ['video/*', ...VIDEO_EXTENSIONS].join(',');

/** Для react-dropzone: тот же список, но в его формате `{ mime: [ext] }`. */
export const VIDEO_DROPZONE_ACCEPT: Record<string, string[]> = { 'video/*': [...VIDEO_EXTENSIONS] };

const MIME_BY_EXTENSION: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/x-m4v',
  mov: 'video/quicktime',
  mkv: 'video/x-matroska',
  webm: 'video/webm',
  avi: 'video/x-msvideo',
  mts: 'video/mp2t',
  m2ts: 'video/mp2t',
  ts: 'video/mp2t',
  '3gp': 'video/3gpp',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
};

function extensionOf(file: File): string {
  return file.name.toLowerCase().split('.').pop() ?? '';
}

/**
 * MIME для presign. Браузер отдаёт `file.type` пустым для `.mkv` и части
 * `.mov` на Windows/Linux, а сервер отбрасывает всё, что не `video/*`, — так
 * что выводим тип из расширения. Точный контейнер серверу не важен: он
 * смотрит на байты через ffprobe.
 */
export function videoMimeType(file: File): string {
  if (file.type && file.type.startsWith('video/')) return file.type;
  return MIME_BY_EXTENSION[extensionOf(file)] ?? 'video/mp4';
}

/**
 * Extract video metadata from File object
 */
export async function extractVideoMetadata(file: File): Promise<{
  duration: number;
  width: number;
  height: number;
  aspectRatio: AspectRatio;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);

      const width = video.videoWidth;
      const height = video.videoHeight;
      const duration = Math.floor(video.duration);

      // Nearest supported bucket. The old version had no 4:3 case and no
      // fallback beyond '1:1', so a 4:3 clip was labelled square — mirrors
      // `classifyAspect` on the server, which has the final say at ingest.
      const aspectRatio = classifyAspect(width, height);

      resolve({
        duration,
        width,
        height,
        aspectRatio,
      });
    };

    video.onerror = () => {
      reject(new Error('Failed to load video metadata'));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * Generate thumbnail from video
 */
export async function generateThumbnail(file: File, timeInSeconds: number = 1): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Failed to get canvas context'));
      return;
    }

    video.preload = 'metadata';

    video.onloadedmetadata = () => {
      video.currentTime = timeInSeconds;
    };

    video.onseeked = () => {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(URL.createObjectURL(blob));
        } else {
          reject(new Error('Failed to generate thumbnail'));
        }
      }, 'image/jpeg', 0.8);

      window.URL.revokeObjectURL(video.src);
    };

    video.onerror = () => {
      reject(new Error('Failed to load video'));
    };

    video.src = URL.createObjectURL(file);
  });
}

/**
 * Validate video file
 */
export function validateVideoFile(file: File): { valid: boolean; error?: string } {
  const maxSize = 500 * 1024 * 1024; // 500MB
  // Любое видео: контейнер и кодек приводит сервер. Отсекаем только то, что
  // видео не является (по MIME, а при пустом MIME — по расширению).
  const isVideo =
    (file.type && file.type.startsWith('video/')) ||
    (VIDEO_EXTENSIONS as readonly string[]).includes(`.${extensionOf(file)}`);

  if (!isVideo) {
    return {
      valid: false,
      error: 'Неподдерживаемый формат. Загрузите видеофайл (MP4, MOV, MKV, WebM…).',
    };
  }

  if (file.size > maxSize) {
    return {
      valid: false,
      error: 'File too large. Maximum size: 500MB',
    };
  }

  return { valid: true };
}
